import type {
  DeckPosition,
  DeckConfiguration,
} from "@/state/run/useDeckLayoutState";
import type { LabwareObject } from "@/automation/labware/labwareManager";
import type { Node } from "@tiptap/pm/model";
import type { SystemConfig, ConfigStep } from "@/state/configState";
import type { ProtocolExport } from "@/automation/protocolExport";
import { getLabwareDimensions } from "@/automation/utils/labwareDimensions";
import type { VolumeBasedTipOption } from "@/automation/system-config/systemConfigParser";
import {
  shouldExcludeFromTouchedStatus as classifyShouldExclude,
  getHeightClass,
  getTipMaxVolume,
  isTipTray as classifyTipTray,
  isTipBox as classifyTipBox,
  isTipRack as classifyTipRack,
  isReturnRack as classifyReturnRack,
  isTipLabware,
  isTrashDevice,
  isWasteDevice,
  isMagnetLabware,
  isBioshakeLabware,
  isGripperLabware,
  isAdapter,
  isAdapter8Channel,
  isAdapter12Channel,
} from "@/automation/utils/labwareClassification";
import { EditorState } from "@tiptap/pm/state";
import { getLabware } from "@/utils/documentAttributes";

// Import pure functions from tipCalculator for testability
// These can be used directly when types are compatible
import {
  resolveTipTypeFromVolume,
  resolveTipTypesFromVolume,
  isOnePerProtocol as isOnePerProtocolPure,
  shouldTrackTipCapacity as shouldTrackTipCapacityPure,
  getPipettingPattern,
  extractTransferCountFromMetadata,
  transferCountToColumnsUsed,
  calculateTipsNeeded,
  allocateTipboxesForTotalUsage,
  assignStepsToTipboxes,
  generateTipLabware,
  type PipettingPattern,
  type StepTipAssignment,
  type StepTipUsage,
  type LabwareTypeConfig,
  TIPBOX_TOTAL_CAPACITY,
  TIPBOX_COLUMN_CAPACITY,
  TIPBOX_ROW_CAPACITY,
} from "@/automation/utils/tipCalculator";

// Re-export for use in other modules
export {
  resolveTipTypesFromVolume,
  getPipettingPattern,
  extractTransferCountFromMetadata,
  transferCountToColumnsUsed,
  calculateTipsNeeded,
  type PipettingPattern,
  type StepTipAssignment,
};

// Note: resolveTipTypeFromVolume and resolveTipTypesFromVolume are now
// imported directly from tipCalculator.ts - no wrapper needed!


// Helper to check if a labware type is marked as one-per-protocol in config
// This controls whether tips are SHARED across steps (vs each step getting dedicated tips)
function isOnePerProtocol(
  typeId: string,
  activeConfig: SystemConfig | null
): boolean {
  if (!activeConfig || !activeConfig.labware) return false;
  // Convert SystemConfig.labware to LabwareTypeConfig[] for pure function
  return isOnePerProtocolPure(typeId, activeConfig.labware as LabwareTypeConfig[]);
}

// Helper to check if a labware type should track tip capacity (column-wise usage)
// This is separate from onePerProtocol to allow shared tips WITHOUT capacity tracking (Felix)
function shouldTrackTipCapacity(
  typeId: string,
  activeConfig: SystemConfig | null
): boolean {
  if (!activeConfig || !activeConfig.labware) return false;
  // Convert SystemConfig.labware to LabwareTypeConfig[] for pure function
  return shouldTrackTipCapacityPure(typeId, activeConfig.labware as LabwareTypeConfig[]);
}

// Helper function to check if labware should be excluded from touched status
function shouldExcludeFromTouchedStatus(
  labware: LabwareObject,
  activeConfig: SystemConfig | null,
): boolean {
  return classifyShouldExclude(labware.typeId, activeConfig);
}

// Helper types for the API call (what the API expects/returns)
interface ApiLabwareObjectRequest {
  id: string;
  typeId: string;
  label: string;
  heightClass?: string | null;
  tags?: string[];
  allowedDeckAreas?: string[] | null;
  allowedPositions?: string[] | null;
  preferredDeckArea?: string | null;
  preferredPositions?: string[] | null;
  tip_details?: { volume_ul: number } | null;
  consumable_type?: string | null;
  volume_ul?: number | null;
  count?: number | null;
  is8chTouched?: boolean;
  is12chTouched?: boolean;
  is1chTouched?: boolean;
  selectedRows?: number;
  selectedColumns?: number;
}

interface ApiLabwareObjectResponse {
  id: string;
  typeId: string;
  label: string;
  heightClass?: string | null;
  allowedDeckAreas?: string[] | null;
  allowedPositions?: string[] | null;
  preferredDeckArea?: string | null;
  preferredPositions?: string[] | null;
  tip_details?: { volume_ul: number } | null;
  consumable_type?: string | null;
  volume_ul?: number | null;
  count?: number | null;
  is8chTouched?: boolean;
  is12chTouched?: boolean;
  is1chTouched?: boolean;
}

interface ApiDeckPosition {
  id: string;
  deckArea: string;
  x: number;
  y: number;
  label: string | null;
  currentLabware: ApiLabwareObjectResponse | null;
}

interface ApiLayoutResponse {
  assignments: Record<string, string> | null;
  unassigned_labware: ApiLabwareObjectResponse[];
  status: string;
  objective_value: number | null;
  deck_positions: ApiDeckPosition[];
}

/**
 * Interface representing metadata for labware in select values
 */
export interface LabwareMetadata {
  selectedRows?: number;
  selectedColumns?: number;
  selectedStartWellAddresses?: [number, number][];
  [key: string]: any; // Allow for additional properties
}

/**
 * Derive classification tags for a labware item.
 *
 * Primary path: use tags parsed from the XML config (set during catalog
 * migration and emitted by the config generator). This is the unified
 * Firestore → XML → parser → solver flow.
 *
 * Fallback path: re-derive from structured config fields when tags are
 * absent (backward compat with old configs that pre-date tag emission).
 */
function deriveLabwareTags(
  typeId: string,
  config: SystemConfig,
): string[] {
  // A tip-RETURN rack: the head places tips INTO it column-by-column, so the
  // solver must treat it like a tip box (reachable placement / tip-reach rules),
  // NOT use its raw "return" config tag (which the solver doesn't understand and
  // would otherwise place anywhere). Derive from the box/rack type + capacity.
  if (classifyReturnRack(typeId, config)) {
    const tags: string[] = [];
    if (classifyTipBox(typeId, config)) tags.push("tipbox");
    else if (classifyTipRack(typeId, config)) tags.push("tiprack");
    const lw = config.labware?.find((l: any) => l.id === typeId);
    // Tall only when it accepts 1 mL tips (same rule as 1 mL source tip boxes).
    if (lw?.supportedTipVolumes?.includes(1000)) tags.push("tall");
    return tags;
  }

  // Primary: use tags from parsed XML config if available
  const lwEntry = config.labware?.find((l: any) => l.id === typeId);
  if (lwEntry?.tags?.length) return [...lwEntry.tags];

  // Fallback: re-derive from structured fields (old configs without tags)
  const tags: string[] = [];
  if (isTipLabware(typeId, config)) tags.push("tip");
  if (classifyTipTray(typeId, config)) tags.push("tiptray");
  if (classifyTipBox(typeId, config)) tags.push("tipbox");
  if (classifyTipRack(typeId, config)) tags.push("tiprack");
  if (isTrashDevice(typeId, config)) tags.push("trash");
  if (isWasteDevice(typeId, config)) tags.push("waste");
  if (isMagnetLabware(typeId, config)) tags.push("magnet");
  if (isBioshakeLabware(typeId, config)) tags.push("bioshake");
  if (isGripperLabware(typeId, config)) tags.push("gripper");
  if (isAdapter(typeId, config)) tags.push("adapter");
  if (isAdapter8Channel(typeId, config)) tags.push("8channel");
  if (isAdapter12Channel(typeId, config)) tags.push("12channel");
  // Height classification: only genuinely tall items get "tall".
  // Trash/waste is tall. For tips, only 1000µL tipboxes/racks are tall
  // (the pipette stays high). Smaller tips (250µL, 50µL) are short —
  // the pipette descends low and would crash into adjacent tall items.
  // Tip trays on stand105 are "tallest" (prongs block all neighbors).
  if (isTrashDevice(typeId, config) || isWasteDevice(typeId, config)) {
    tags.push("tall");
  } else if (
    (classifyTipBox(typeId, config) || classifyTipRack(typeId, config)) &&
    (getTipMaxVolume(typeId, config) ?? 0) >= 1000
  ) {
    tags.push("tall");
  } else if (getHeightClass(typeId, config) === "tall" && !isTipLabware(typeId, config)) {
    // Non-tip tall items (e.g. deep-well plates with stackHeight > 40mm)
    tags.push("tall");
  }
  const lwEntry2 = config.labware?.find((l: any) => l.id === typeId);
  if (lwEntry2?.tipStand === "stand105") {
    tags.push("tallest");
    if (!tags.includes("tall")) tags.push("tall");
  }
  return tags;
}

/**
 * Assigns labware to deck positions based on compatibility and availability
 * by calling the deck heuristics API.
 *
 * @param requiredLabware Array of labware required for the protocol (conforming to local LabwareObject type)
 * @param deckConfig Current deck configuration
 * @param protocol The exported protocol containing all steps
 * @param activeConfig The active system configuration
 * @param fixedAssignments Optional map of labware-id to position-id for fixed assignments
 * @returns A Promise resolving to the updated deck configuration with assigned labware and an array of unassigned labware
 */
export async function assignLabwareToDeck(
  requiredLabware: LabwareObject[],
  deckConfig: DeckConfiguration,
  protocol: ProtocolExport,
  activeConfig: SystemConfig,
  fixedAssignments?: Record<string, string>
): Promise<{
  updatedConfig: DeckConfiguration;
  unassignedLabware: LabwareObject[];
}> {
  // --- Spanning ("covers") positions, e.g. the Position 13 SELECT nest ---
  // A spanning position covers two underlying positions (A13 -> A1+A4) and is
  // handled entirely client-side: the solver never knows about it. When labware
  // is pinned to one (via fixedAssignments / deck_position_override), we (a) pull
  // that labware out of the solver request, (b) tell the solver to avoid the
  // covered positions, and (c) drop the pinned labware onto the spanning
  // position ourselves after the solver returns.
  const coveringById = new Map<string, DeckPosition>();
  for (const p of deckConfig.positions) {
    if (p.coversPositions?.length) coveringById.set(p.id, p);
  }
  const activeCoverPins = new Map<string, string>(); // coveringPosId -> labwareId
  const blockedPositionIds = new Set<string>();
  const clientPinnedLabwareIds = new Set<string>();
  if (fixedAssignments) {
    for (const [lwId, posId] of Object.entries(fixedAssignments)) {
      const cover = coveringById.get(posId);
      if (!cover) continue;
      activeCoverPins.set(posId, lwId);
      clientPinnedLabwareIds.add(lwId);
      for (const covered of cover.coversPositions ?? [])
        blockedPositionIds.add(covered);
    }
  }
  // Everything the solver should place (excludes client-pinned spanning labware).
  const solverLabware = requiredLabware.filter(
    (lw) => !clientPinnedLabwareIds.has(lw.id)
  );
  // Fixed assignments minus spanning pins (the solver doesn't have those positions).
  const solverFixedAssignments: Record<string, string> = {};
  if (fixedAssignments) {
    for (const [lwId, posId] of Object.entries(fixedAssignments)) {
      if (!clientPinnedLabwareIds.has(lwId)) solverFixedAssignments[lwId] = posId;
    }
  }

  // Map TS LabwareObject to the structure expected by the API for the request.
  // Only include fields known to be on the TS LabwareObject type to avoid type errors.
  // The API will use defaults for other fields if they are not provided.
  const apiRequestLabware: ApiLabwareObjectRequest[] = solverLabware.map(
    (lw) => {
      const is8ch = isLabware8chTouched(lw, protocol, activeConfig);
      const is1ch = isLabware1chTouched(lw, protocol, activeConfig);

      // Get dimensions from config
      let selectedRows: number | undefined = undefined;
      let selectedColumns: number | undefined = undefined;
      const dims = getLabwareDimensions(lw.typeId, activeConfig);
      if (dims && (dims[0] > 0 || dims[1] > 0)) {
        selectedRows = dims[0];
        selectedColumns = dims[1];
      }

      // Derive classification tags from structured config data.
      // Tags are the source of truth for height — they come from the catalog
      // and distinguish actually-tall items (waste, 1000µL tips) from short ones
      // (250µL tipbox). Derive heightClass from tags to keep them consistent.
      const tags = deriveLabwareTags(lw.typeId, activeConfig);
      const heightClassValue = tags.includes("tall") ? "tall" : null;

      return {
        id: lw.id,
        typeId: lw.typeId,
        label: lw.label,
        heightClass: heightClassValue,
        tags: tags.length > 0 ? tags : undefined,
        allowedDeckAreas: (lw as any).allowedDeckAreas || null,
        allowedPositions: (lw as any).allowedPositions || null,
        preferredDeckArea: (lw as any).preferredDeckArea || null,
        preferredPositions: (lw as any).preferredPositions || null,
        tip_details: (lw as any).tip_details || null,
        consumable_type: (lw as any).consumable_type || null,
        volume_ul: (lw as any).volume_ul || null,
        count: (lw as any).count || null,
        is8chTouched: is8ch ? true : undefined,
        is12chTouched: isLabware12chTouched(lw, protocol, activeConfig)
          ? true
          : undefined,
        is1chTouched: is1ch ? true : undefined,
        selectedRows,
        selectedColumns,
      };
    }
  );

  // Check if this is a Felix deck layout (API only supports Felix positions)
  const isFelixLayout = deckConfig.positions.some(
    (p) => /^[A-C]\d+$/.test(p.id) // Felix positions are like A1, B7, C10
  );

  let result: ApiLayoutResponse;

  try {
    if (isFelixLayout) {
      // Use the cloud API for Felix layouts
      const requestBody = {
        required_labware: apiRequestLabware,
        fixed_assignments: solverFixedAssignments,
        // Positions the solver must leave empty (covered by an active spanning
        // position such as the Position 13 nest). Empty unless a span is in use.
        blocked_positions: [...blockedPositionIds],
        // Selected head — SELECT heads ("8_*") relax tall-adjacency rules in the
        // solver (channels descend individually, ~50mm extra clearance).
        head_type: protocol.selectedHead,
      };

      const apiUrl =
        import.meta.env.VITE_DECK_LAYOUT_URL ||
        "https://deck-layout-181245091241.europe-west4.run.app/layout/";

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Deck layout API call failed:", response.status, errorText);
        throw new Error(
          `API call to /layout failed with status ${response.status}: ${errorText}`
        );
      }

      result = await response.json();
    } else {
      // For non-Felix robots, do simple sequential assignment locally
      console.log("[DeckLayout] Using local sequential assignment for non-Felix robot");

      const assignments: Record<string, string> = { ...solverFixedAssignments };
      const assignedPositions = new Set(Object.values(assignments));
      const unassigned: LabwareObject[] = [];

      // Get available positions: skip fixed, spanning (handled client-side),
      // blocked (covered by an active span), and already-assigned positions.
      const availablePositions = deckConfig.positions
        .filter(
          (p) =>
            !p.fixedLabware &&
            !p.coversPositions?.length &&
            !blockedPositionIds.has(p.id) &&
            !assignedPositions.has(p.id)
        )
        .map((p) => p.id);

      let positionIndex = 0;

      // Assign labware sequentially to available positions
      for (const lw of solverLabware) {
        // Skip if already has a fixed assignment
        if (assignments[lw.id]) continue;

        if (positionIndex < availablePositions.length) {
          assignments[lw.id] = availablePositions[positionIndex];
          positionIndex++;
        } else {
          unassigned.push(lw);
        }
      }

      result = {
        assignments,
        unassigned_labware: unassigned.map((lw) => ({
          id: lw.id,
          typeId: lw.typeId,
          label: lw.label,
        })),
        status: "success",
        objective_value: null,
        deck_positions: [],
      };
    }

    const updatedDeckConfig: DeckConfiguration = {
      id: deckConfig.id,
      label: deckConfig.label,
      positions: [],
      sections: JSON.parse(JSON.stringify(deckConfig.sections)),
    };

    const originalPositionsMap = new Map(
      deckConfig.positions.map((p) => [p.id, p])
    );

    // Create a map from labware ID to labware object for quick lookup
    const labwareMap = new Map(requiredLabware.map((lw) => [lw.id, lw]));

    // Build a reverse map: position ID -> labware ID from API assignments
    const positionToLabwareId = new Map<string, string>();
    if (result.assignments) {
      for (const [labwareId, positionId] of Object.entries(result.assignments)) {
        positionToLabwareId.set(positionId, labwareId);
      }
    }

    // Safety guard: a covered position (A1/A4 under an active A13 nest) is
    // physically under the spanning adapter and must never hold labware. The
    // solver avoids these via blocked_positions; choosing a *crash-safe,
    // accessible* alternative is the solver's job (tall-neighbour, tip-reach and
    // deck-area rules), so we do NOT guess a spot here. If the solver still
    // placed something on a covered position (e.g. an old deployment that
    // ignores blocked_positions), surface it as unassigned — visible, and the
    // run is blocked until the solver places it correctly.
    if (blockedPositionIds.size > 0) {
      for (const blockedId of blockedPositionIds) {
        const lwId = positionToLabwareId.get(blockedId);
        if (!lwId) continue;
        positionToLabwareId.delete(blockedId);
        const lw = labwareMap.get(lwId);
        if (lw) {
          result.unassigned_labware.push({
            id: lw.id,
            typeId: lw.typeId,
            label: lw.label,
          } as ApiLabwareObjectResponse);
        }
      }
    }

    // Preserve original positions from config and apply labware assignments
    // This ensures we use the correct deck positions from the XML config,
    // not the hardcoded Felix positions from the API
    updatedDeckConfig.positions = deckConfig.positions.map(
      (originalPos: DeckPosition) => {
        // A client-side spanning pin (e.g. source plate on the Position 13 nest)
        // takes precedence over any solver assignment for this position.
        const pinnedLabwareId = activeCoverPins.get(originalPos.id);
        const assignedLabwareId =
          pinnedLabwareId ?? positionToLabwareId.get(originalPos.id);
        let currentLabwareTS: LabwareObject | undefined = undefined;

        if (assignedLabwareId) {
          const sourceLw = labwareMap.get(assignedLabwareId);
          if (sourceLw) {
            currentLabwareTS = { ...sourceLw };
          }
        }

        // Also check API response for any additional labware data if we didn't 
        // already get labware from the assignments map
        if (!currentLabwareTS) {
          const apiPos = result.deck_positions.find(
            (ap: ApiDeckPosition) => ap.id === originalPos.id
          );

          if (apiPos?.currentLabware) {
            const apiLw = apiPos.currentLabware;
            currentLabwareTS = {
              id: apiLw.id,
              typeId: apiLw.typeId,
              label: apiLw.label,
            };
            // Copy additional fields if present
            if ("heightClass" in apiLw) (currentLabwareTS as any).heightClass = apiLw.heightClass;
            if ("tip_details" in apiLw) (currentLabwareTS as any).tip_details = apiLw.tip_details;
            if ("is8chTouched" in apiLw) (currentLabwareTS as any).is8chTouched = apiLw.is8chTouched;
            if ("is12chTouched" in apiLw) (currentLabwareTS as any).is12chTouched = apiLw.is12chTouched;
            if ("is1chTouched" in apiLw) (currentLabwareTS as any).is1chTouched = apiLw.is1chTouched;
          }
        }

        // Check verification status
        const prevPos = originalPositionsMap.get(originalPos.id);
        let isVerified = false;
        if (
          prevPos?.currentLabware &&
          currentLabwareTS &&
          prevPos.currentLabware.id === currentLabwareTS.id
        ) {
          isVerified = prevPos.isVerified || false;
        }

        // Return position using original config structure, not API structure
        return {
          id: originalPos.id,
          allowedLabware: originalPos.allowedLabware,
          fixedLabware: originalPos.fixedLabware,
          coversPositions: originalPos.coversPositions,
          currentLabware: currentLabwareTS,
          isVerified: isVerified,
        };
      }
    );

    const unassignedLabwareTS: LabwareObject[] = result.unassigned_labware.map(
      (apiLw: ApiLabwareObjectResponse) => {
        // Map API response labware to local LabwareObject type.
        const tsLw: LabwareObject = {
          id: apiLw.id,
          typeId: apiLw.typeId,
          label: apiLw.label,
        };
        // Assign other properties if they are expected on the local LabwareObject type
        if ("heightClass" in apiLw) {
          (tsLw as any).heightClass = apiLw.heightClass || undefined;
        }
        if ("allowedDeckAreas" in apiLw) {
          (tsLw as any).allowedDeckAreas = apiLw.allowedDeckAreas || undefined;
        }
        if ("allowedPositions" in apiLw) {
          (tsLw as any).allowedPositions = apiLw.allowedPositions || undefined;
        }
        if ("preferredDeckArea" in apiLw) {
          (tsLw as any).preferredDeckArea =
            apiLw.preferredDeckArea || undefined;
        }
        if ("preferredPositions" in apiLw) {
          (tsLw as any).preferredPositions =
            apiLw.preferredPositions || undefined;
        }
        if ("tip_details" in apiLw) {
          (tsLw as any).tip_details = apiLw.tip_details || undefined;
        }
        if ("consumable_type" in apiLw) {
          (tsLw as any).consumable_type = apiLw.consumable_type || undefined;
        }
        if ("volume_ul" in apiLw) {
          (tsLw as any).volume_ul = apiLw.volume_ul || undefined;
        }
        if ("count" in apiLw) {
          (tsLw as any).count = apiLw.count || undefined;
        }
        // Add is8chTouched to unassigned LabwareObject if present in API response
        if ("is8chTouched" in apiLw) {
          (tsLw as any).is8chTouched = apiLw.is8chTouched;
        }
        if ("is12chTouched" in apiLw) {
          (tsLw as any).is12chTouched = apiLw.is12chTouched;
        }
        if ("is1chTouched" in apiLw) {
          (tsLw as any).is1chTouched = apiLw.is1chTouched;
        }
        return tsLw;
      }
    );

    return {
      updatedConfig: updatedDeckConfig,
      unassignedLabware: unassignedLabwareTS,
    };
  } catch (error) {
    console.error("Error in assignLabwareToDeck (API call):", error);
    return {
      updatedConfig: JSON.parse(JSON.stringify(deckConfig)),
      unassignedLabware: JSON.parse(JSON.stringify(requiredLabware)),
    };
  }
}

/**
 * Determines if a labware object is touched by an 8-channel pipetting pattern
 * in any of the protocol steps it's used in.
 *
 * @param labware The labware object to check.
 * @param protocol The exported protocol containing all steps.
 * @param activeConfig The active system configuration.
 * @returns True if the labware is touched by an 8-channel pattern, false otherwise.
 */
export function isLabware8chTouched(
  labware: LabwareObject,
  protocol: ProtocolExport,
  activeConfig: SystemConfig | null
): boolean {
  if (!activeConfig || !activeConfig.steps) {
    return false;
  }

  const labwareId = labware.id;

  // Check if labware should be excluded from touched status
  if (shouldExcludeFromTouchedStatus(labware, activeConfig)) {
    return false;
  }

  for (const step of protocol.steps) {
    // Check if the current labware is used in this step's parameters
    let labwareUsedInStep = false;
    for (const param of step.parameters) {
      if (param.value && typeof param.value === "object") {
        if (Array.isArray(param.value)) {
          for (const item of param.value) {
            if (
              item &&
              typeof item === "object" &&
              "type" in item &&
              item.type === "labware" &&
              item.labware &&
              item.labware.id === labwareId
            ) {
              labwareUsedInStep = true;
              break;
            }
          }
        } else if (
          "type" in param.value &&
          param.value.type === "labware" &&
          (param.value as any).labware &&
          (param.value as any).labware.id === labwareId
        ) {
          labwareUsedInStep = true;
        }
      }
      if (labwareUsedInStep) break;
    }

    if (labwareUsedInStep) {
      const stepConfig = activeConfig.steps.find(
        (s: ConfigStep) =>
          s.id === step.configId || s.id === step.id || s.name === step.name
      );

      if (stepConfig && stepConfig.compatiblePipettingPatterns) {
        // Check compatiblePipettingPatterns array
        for (const pattern of stepConfig.compatiblePipettingPatterns) {
          if (pattern === "8x1" || pattern === "8-channel") {
            return true;
          }
        }
      }
    }
  }

  return false;
}

/**
 * Determines if a labware object is touched by a 12-channel pipetting pattern
 * in any of the protocol steps it's used in.
 *
 * @param labware The labware object to check.
 * @param protocol The exported protocol containing all steps.
 * @param activeConfig The active system configuration.
 * @returns True if the labware is touched by a 12-channel pattern, false otherwise.
 */
export function isLabware12chTouched(
  labware: LabwareObject,
  protocol: ProtocolExport,
  activeConfig: SystemConfig | null
): boolean {
  if (!activeConfig || !activeConfig.steps) {
    return false;
  }

  const labwareId = labware.id;

  // Check if labware should be excluded from touched status
  if (shouldExcludeFromTouchedStatus(labware, activeConfig)) {
    return false;
  }

  for (const step of protocol.steps) {
    // Check if the current labware is used in this step's parameters
    let labwareUsedInStep = false;
    for (const param of step.parameters) {
      if (param.value && typeof param.value === "object") {
        if (Array.isArray(param.value)) {
          for (const item of param.value) {
            if (
              item &&
              typeof item === "object" &&
              "type" in item &&
              item.type === "labware" &&
              item.labware &&
              item.labware.id === labwareId
            ) {
              labwareUsedInStep = true;
              break;
            }
          }
        } else if (
          "type" in param.value &&
          param.value.type === "labware" &&
          (param.value as any).labware &&
          (param.value as any).labware.id === labwareId
        ) {
          labwareUsedInStep = true;
        }
      }
      if (labwareUsedInStep) break;
    }

    if (labwareUsedInStep) {
      const stepConfig = activeConfig.steps.find(
        (s: ConfigStep) =>
          s.id === step.configId || s.id === step.id || s.name === step.name
      );

      if (stepConfig && stepConfig.compatiblePipettingPatterns) {
        // Check compatiblePipettingPatterns array
        for (const pattern of stepConfig.compatiblePipettingPatterns) {
          if (pattern === "1x12" || pattern === "12-channel") {
            return true;
          }
        }
      }
    }
  }

  return false;
}

/**
 * Determines if a labware object is touched by a 1-channel pipetting pattern
 * in any of the protocol steps it's used in.
 *
 * @param labware The labware object to check.
 * @param protocol The exported protocol containing all steps.
 * @param activeConfig The active system configuration.
 * @returns True if the labware is touched by a 1-channel pattern, false otherwise.
 */
export function isLabware1chTouched(
  labware: LabwareObject,
  protocol: ProtocolExport,
  activeConfig: SystemConfig | null
): boolean {
  if (!activeConfig || !activeConfig.steps) {
    return false;
  }

  const labwareId = labware.id;

  // Check if labware should be excluded from touched status
  if (shouldExcludeFromTouchedStatus(labware, activeConfig)) {
    return false;
  }

  for (const step of protocol.steps) {
    // Check if the current labware is used in this step's parameters
    let labwareUsedInStep = false;
    for (const param of step.parameters) {
      if (param.value && typeof param.value === "object") {
        if (Array.isArray(param.value)) {
          for (const item of param.value) {
            if (
              item &&
              typeof item === "object" &&
              "type" in item &&
              item.type === "labware" &&
              item.labware &&
              item.labware.id === labwareId
            ) {
              labwareUsedInStep = true;
              break;
            }
          }
        } else if (
          "type" in param.value &&
          param.value.type === "labware" &&
          (param.value as any).labware &&
          (param.value as any).labware.id === labwareId
        ) {
          labwareUsedInStep = true;
        }
      }
      if (labwareUsedInStep) break;
    }

    if (labwareUsedInStep) {
      const stepConfig = activeConfig.steps.find(
        (s: ConfigStep) =>
          s.id === step.configId || s.id === step.id || s.name === step.name
      );

      if (stepConfig && stepConfig.compatiblePipettingPatterns) {
        // Check compatiblePipettingPatterns array
        for (const pattern of stepConfig.compatiblePipettingPatterns) {
          if (pattern === "1x1" || pattern === "1-channel") {
            return true;
          }
        }
      }
    }
  }

  return false;
}

/**
 * Validates if the deck configuration is ready for execution
 *
 * @param deckConfig Current deck configuration
 * @returns Boolean indicating if the deck is ready
 */
export function isDeckReady(deckConfig: DeckConfiguration): boolean {
  return deckConfig.positions.every(
    (position) => !position.currentLabware || position.isVerified
  );
}

// Helper function to get step name from node
function getStepName(node: Node): string {
  let stepName = "";
  node.descendants((childNode: Node) => {
    if (childNode.type.name === "stepName" && childNode.textContent) {
      stepName = childNode.textContent;
      return false;
    }
    return true;
  });
  return stepName;
}

/**
 * Gets the parameter name and stable configParamId for a selectValue node by
 * traversing up to find the parent parameter node.
 */
function getParameterIdForSelectValue(
  startPos: number, // Start position of the selectValue node
  doc: Node
): { paramName: string | null; configParamId: string | null } {
  const $resolvedPos = doc.resolve(startPos);
  let currentDepth = $resolvedPos.depth;

  while (currentDepth > 0) {
    const parent = $resolvedPos.node(currentDepth);

    if (parent.type.name === "parameter") {
      let paramName: string | null = null;
      for (let i = 0; i < parent.childCount; i++) {
        const child = parent.child(i);
        if (child.type.name === "paramName") {
          paramName = child.textContent;
          break;
        }
      }
      return {
        paramName,
        configParamId:
          (parent.attrs.configParamId as string | null | undefined) ?? null,
      };
    }

    currentDepth--;
  }

  return { paramName: null, configParamId: null };
}

/**
 * Calculates the required tip labware based on steps and source labware metadata,
 * and creates a map linking source labware to its required tip labware.
 * 
 * TIP COUNTING RULES:
 * - SOURCE wells/columns/rows govern new tip usage (not destination)
 * - EXCEPTION: If "dispense from bottom" is enabled, DESTINATION wells/columns/rows govern instead
 * - 96-channel tip trays are EXCLUDED - they use a separate allocation system
 * 
 * @param state The editor state containing the document
 * @param activeConfig The active system configuration
 * @returns An object containing:
 *   - tipLabware: Array of required tip labware objects
 *   - sourceToTipMap: Map linking source labware ID to its required tip labware ID
 *   - stepToTipMap: Map linking step index to its tip labware ID
 */
// Note: TIPBOX_COLUMN_CAPACITY, TIPBOX_ROW_CAPACITY, TIPBOX_TOTAL_CAPACITY, 
// PipettingPattern, getPipettingPattern, extractTransferCountFromMetadata, 
// and transferCountToColumnsUsed are now imported directly from tipCalculator.ts

/**
 * Checks if a step has "dispense from bottom" enabled
 * When enabled, destination wells govern tip usage instead of source wells
 * @param stepNode The step node from the document
 * @returns True if dispense from bottom is enabled
 */
function isDispenseFromBottomEnabled(stepNode: Node): boolean {
  const stepSettings = stepNode.attrs?.stepSettings as any;
  if (!stepSettings) return false;

  // Check dispense.fromBottom in step settings
  return stepSettings?.dispense?.fromBottom === true;
}


/**
 * Reads the tip count stored on a CSV-map parameter's value node.
 *
 * CSV steps reuse one tip per source-well group, so their tip count cannot be
 * derived from well selection. Instead it is computed at upload time (the number
 * of consecutive source-well groups in the worklist) and stored on the paramValue
 * node as `csvTipCount`. Returns null when no count is stored (e.g. protocols
 * created before this was tracked, or no CSV linked yet).
 */
function readCsvTipCount(
  stepNode: Node,
  csvParamId: string,
  csvParamName: string
): number | null {
  let result: number | null = null;
  stepNode.descendants((paramNode: Node) => {
    if (result !== null) return false; // already found
    if (paramNode.type.name !== "parameter") return true;

    const configParamId = paramNode.attrs.configParamId as string | null;
    const paramName = paramNode.firstChild?.textContent || "";
    const matches = configParamId
      ? configParamId === csvParamId
      : paramName.toLowerCase() === csvParamName.toLowerCase();
    if (!matches) return true;

    paramNode.descendants((valueNode: Node) => {
      if (valueNode.type.name !== "paramValue") return true;
      const raw = valueNode.attrs.csvTipCount;
      if (typeof raw === "number" && Number.isFinite(raw)) {
        result = raw;
      } else if (typeof raw === "string" && raw.trim() !== "") {
        const parsed = parseInt(raw, 10);
        if (!Number.isNaN(parsed)) result = parsed;
      }
      return false; // found the value node
    });
    return false; // found the matching parameter
  });
  return result;
}

export function calculateRequiredTipLabware(
  state: EditorState,
  activeConfig: SystemConfig | null
): {
  tipLabware: LabwareObject[];
  sourceToTipMap: Map<string, string>;
  stepToTipMap: Map<number, string>;
  /** Per-step tip usage for display, keyed by step uid. */
  stepTipUsageByUid: Map<string, StepTipUsage>;
} {
  if (!activeConfig)
    return {
      tipLabware: [],
      sourceToTipMap: new Map(),
      stepToTipMap: new Map(),
      stepTipUsageByUid: new Map(),
    };

  // Get labware map from document to access source labware names
  const labwareOnDoc = getLabware(state);
  const labwareMap = new Map<string, LabwareObject>(
    labwareOnDoc.map((lw) => [lw.id, lw])
  );

  // Map to track source labware IDs and their required tip type ID
  // Key: sourceLabwareId + tipTypeId (composite key)
  // Value: { sourceId: string, requiredTipTypeId: string }
  const sourceLabwareTipGroups = new Map<
    string,
    { sourceId: string; requiredTipTypeId: string }
  >();

  // Map to track tip assignments for each source and tip type combination
  const sourceToTipMap = new Map<string, string>();

  // Per-step tip usage for display on the tip allocation screen (keyed by step uid).
  // Independent of which tip type is selected — the UI applies tray exclusion and
  // per-type totals using the step's currently selected tip.
  const stepTipUsageByUid = new Map<string, StepTipUsage>();

  // ========== Tipbox capacity tracking ==========
  // Track cumulative column usage per shared tip type
  // tipTypeId -> { currentUsage: number, instances: string[] }
  const sharedTipboxTracking = new Map<
    string,
    { currentUsage: number; instances: string[] }
  >();

  // Track which tipbox instance each step should use
  // stepIndex -> tipLabwareId
  const stepToTipboxInstance = new Map<number, string>();

  // Counter for generating unique tipbox IDs
  let tipboxIdCounter = 0;
  // ===================================================

  // Track shared tipbox IDs per tip type (supports multiple one-per-protocol types)
  // NOTE: This now tracks the CURRENT (most recent) tipbox for each type
  const sharedTipboxIdsByType = new Map<string, string>();

  // Track step index and assignments discovered during traversal
  let stepIndexCounter = -1;
  const stepAssignments: Array<{
    stepIndex: number;
    sourceId: string;
    tipTypeId: string;
    transferCount: number; // Raw number of tips/columns/rows needed
    columnsUsed: number; // Rounded-up equivalent columns (for compatibility)
    pattern: PipettingPattern;
  }> = [];

  // ========== Helper to allocate tipboxes based on total usage (order-independent) ==========
  // This is called AFTER document traversal to allocate the right number of tipboxes
  function allocateTipboxesForTotalUsage(
    tipTypeId: string,
    totalTipsNeeded: number
  ): string[] {
    const numTipboxes = Math.ceil(totalTipsNeeded / TIPBOX_TOTAL_CAPACITY);
    const tipboxIds: string[] = [];

    for (let i = 0; i < numTipboxes; i++) {
      tipboxIdCounter++;
      tipboxIds.push(`tips-${tipboxIdCounter}`);
    }

    // Store in tracking map for later tipLabware generation
    sharedTipboxTracking.set(tipTypeId, {
      currentUsage: totalTipsNeeded % TIPBOX_TOTAL_CAPACITY || (numTipboxes > 0 ? TIPBOX_TOTAL_CAPACITY : 0),
      instances: tipboxIds,
    });

    return tipboxIds;
  }
  // ===================================================================

  // Find all steps with tip parameters and identify associated source labware
  // Traverse the document steps
  state.doc.descendants((node: Node, stepPos: number) => {
    if (node.type.name === "step") {
      // Increment step index for each step encountered in document order
      stepIndexCounter += 1;
      const stepName = getStepName(node);
      const stepUid = node.attrs.uid as string | null;
      const configStepId = node.attrs.configStepId as string | null;
      const stepConfig =
        (configStepId
          ? activeConfig.steps.find((s: ConfigStep) => s.id === configStepId)
          : undefined) ??
        activeConfig.steps.find((s: ConfigStep) => s.name === stepName);

      if (!stepConfig || !stepConfig.params) return;

      // Determine the pipetting pattern for this step
      const pipettingPattern = getPipettingPattern(stepConfig);



      // Find the tip parameter for this step
      const tipParamConfig = stepConfig.params.find((p) => p.role === "tips");
      if (
        !tipParamConfig ||
        ((!tipParamConfig.options || tipParamConfig.options.length === 0) &&
          (!tipParamConfig.volumeOptions ||
            tipParamConfig.volumeOptions.length === 0))
      ) {
        return; // No tip parameter or no tip options defined for this step
      }

      // Check if dispense from bottom is enabled (affects whether source or dest governs tips)
      const dispenseFromBottom = isDispenseFromBottomEnabled(node);

      // Get the default tip type (fallback for volume-based selection)
      const defaultTipTypeId = tipParamConfig.options?.[0] || "";

      // Extract volume parameter from this step instance if volume-based options exist
      let resolvedTipTypeIds: string[] = tipParamConfig.options || [
        defaultTipTypeId,
      ];

      // Only use volume-based selection if volume options exist and volume parameter is found
      if (
        tipParamConfig.volumeOptions &&
        tipParamConfig.volumeOptions.length > 0
      ) {
        const volumeParams = stepConfig.params.filter(
          (p) => p.type === "volume"
        );
        if (volumeParams.length > 0) {
          // Extract volume value from step instance
          let volumeValue: number | null = null;
          node.descendants((paramNode: Node) => {
            if (paramNode.type.name === "parameter") {
              const paramNameNode = paramNode.firstChild;
              const paramName = paramNameNode?.textContent || "";
              const configParamId = paramNode.attrs.configParamId as
                | string
                | null;

              const matches = configParamId
                ? volumeParams.some((vp) => vp.id === configParamId)
                : volumeParams.some(
                    (vp) => vp.name.toLowerCase() === paramName.toLowerCase()
                  );

              if (matches) {
                const paramValueNode = paramNode.lastChild;
                const textValue = paramValueNode?.textContent || "";
                const parsedValue = parseFloat(textValue);
                if (!isNaN(parsedValue)) {
                  volumeValue = parsedValue;
                }
              }
            }
          });

          // Use volume-based selection only if we found a valid volume
          if (volumeValue !== null) {
            resolvedTipTypeIds = resolveTipTypesFromVolume(
              volumeValue,
              tipParamConfig.volumeOptions,
              defaultTipTypeId
            );
          }
        }
      }

      // Find source and destination parameters from step config
      const sourceParams = stepConfig.params.filter((p) => p.role === "source");
      const destinationParams = stepConfig.params.filter((p) => p.role === "destination");

      // Collect source and destination metadata from the step
      let sourceMetadata: any = null;
      let destinationMetadata: any = null;
      let sourceId: string | null = null;

      node.descendants((paramNode: Node, relativePos: number) => {
        if (paramNode.type.name === "selectValue" && paramNode.attrs.selectedId) {
          const absolutePos = stepPos + 1 + relativePos;
          const { paramName, configParamId } = getParameterIdForSelectValue(
            absolutePos,
            state.doc
          );

          if (paramName) {
            const isSource = configParamId
              ? sourceParams.some((p) => p.id === configParamId)
              : sourceParams.some(
                  (p) => p.name.toLowerCase() === paramName.toLowerCase()
                );
            const isDestination = configParamId
              ? destinationParams.some((p) => p.id === configParamId)
              : destinationParams.some(
                  (p) => p.name.toLowerCase() === paramName.toLowerCase()
                );

            if (isSource) {
              sourceId = paramNode.attrs.selectedId;
              sourceMetadata = paramNode.attrs.metadata;
            }
            if (isDestination) {
              destinationMetadata = paramNode.attrs.metadata;
            }
          }
        }
      });

      // If no source found, skip this step
      if (!sourceId) {

        return;
      }

      // Determine which metadata governs tip usage
      // - Normally: SOURCE governs (1 source well = 1 tip, even if multi-dispense)
      // - With dispenseFromBottom: DESTINATION governs (each dest needs fresh tip)
      const governingMetadata = dispenseFromBottom ? destinationMetadata : sourceMetadata;
      const governingRole = dispenseFromBottom ? "destination" : "source";

      // Calculate transfer count from the governing metadata
      const transferCount = extractTransferCountFromMetadata(governingMetadata, pipettingPattern);
      const columnsUsed = transferCountToColumnsUsed(transferCount, pipettingPattern);

      // ----- Record per-step tip usage for display (tip-type independent) -----
      // CSV steps reuse one tip per source-well group, so their count is the
      // stored csvTipCount (computed at upload), not the well-selection count.
      // 96-channel (8x12) steps have no reliable per-step tip count here, so we
      // leave them unknown ("—"); these are almost always whole-tray pickups.
      if (stepUid) {
        const csvParam = stepConfig.params.find((p) => p.type === "csv_map");
        let usage: StepTipUsage;
        if (csvParam) {
          const csvCount = readCsvTipCount(node, csvParam.id, csvParam.name);
          usage = { tips: csvCount, pattern: "1x1", unitCount: csvCount ?? 0 };
        } else if (stepConfig.id === "serial_dilution") {
          // Serial dilution loads ONE column/row of tips and reuses it for the
          // whole series (buffer + all cascading transfers), regardless of how
          // many columns are diluted — so it's always a single pick.
          usage = {
            tips: calculateTipsNeeded(1, pipettingPattern),
            pattern: pipettingPattern,
            unitCount: 1,
          };
        } else if (pipettingPattern === "8x12") {
          usage = { tips: null, pattern: pipettingPattern, unitCount: transferCount };
        } else {
          usage = {
            tips: calculateTipsNeeded(transferCount, pipettingPattern),
            pattern: pipettingPattern,
            unitCount: transferCount,
          };
        }
        stepTipUsageByUid.set(stepUid, usage);
      }
      // ------------------------------------------------------------------------

      // Process each resolved tip type
      for (const currentStepTipTypeId of resolvedTipTypeIds) {
        // Check if this tip type is configured as one-per-protocol (shared across steps)
        const isSharedTipType = isOnePerProtocol(
          currentStepTipTypeId,
          activeConfig
        );

        // Check if this tip type should track capacity (column-wise usage)
        const trackCapacity = shouldTrackTipCapacity(
          currentStepTipTypeId,
          activeConfig
        );

        // For shared tip types, reuse a single global instance
        // For others, use the original source-based mapping
        if (isSharedTipType) {
          // Only do capacity tracking if explicitly enabled
          if (trackCapacity) {
            // Just record the assignment; actual allocation happens after traversal
            // (This makes the algorithm order-independent)
          }

          // Store with a special 'GLOBAL_SHARED_TIPS' source ID to ensure all shared tips are grouped together
          const globalKey = `GLOBAL_SHARED_TIPS:${currentStepTipTypeId}`;
          sourceLabwareTipGroups.set(globalKey, {
            sourceId: "GLOBAL_SHARED_TIPS",
            requiredTipTypeId: currentStepTipTypeId,
          });
          // Record assignment for this step; will resolve to shared tipbox later
          stepAssignments.push({
            stepIndex: stepIndexCounter,
            sourceId,
            tipTypeId: currentStepTipTypeId,
            transferCount,
            columnsUsed,
            pattern: pipettingPattern,
          });
        } else {
          // For non-shared types, continue with the source-specific approach
          // Create a composite key that combines source ID and tip type
          const compositeKey = `${sourceId}:${currentStepTipTypeId}`;

          // Store this source-tip type combination
          sourceLabwareTipGroups.set(compositeKey, {
            sourceId,
            requiredTipTypeId: currentStepTipTypeId,
          });
          // Record assignment for this step for direct mapping later
          stepAssignments.push({
            stepIndex: stepIndexCounter,
            sourceId,
            tipTypeId: currentStepTipTypeId,
            transferCount,
            columnsUsed,
            pattern: pipettingPattern,
          });
        }
      } // End of for loop for resolvedTipTypeIds
    }
  });

  // ========== POST-TRAVERSAL: Order-Independent Tipbox Allocation ==========
  // Now that we have all step assignments, calculate total tips per tip type
  // and allocate the correct number of tipboxes (order-independent)

  // Group step assignments by tip type and calculate totals
  const totalTipsByTipType = new Map<string, number>();
  for (const assignment of stepAssignments) {
    // Only process shared tip types with capacity tracking
    const isShared = isOnePerProtocol(assignment.tipTypeId, activeConfig);
    const trackCap = shouldTrackTipCapacity(assignment.tipTypeId, activeConfig);

    if (isShared && trackCap) {
      const current = totalTipsByTipType.get(assignment.tipTypeId) || 0;
      // Calculate actual tips needed based on pattern
      let tipsNeeded: number;
      switch (assignment.pattern) {
        case "1x1":
          // 1-channel: transferCount is already individual tips
          tipsNeeded = assignment.transferCount;
          break;
        case "8x1":
          // 8-channel: transferCount is columns, each column = 8 tips
          tipsNeeded = assignment.transferCount * 8;
          break;
        case "1x12":
          // 12-channel: transferCount is rows, each row = 12 tips
          tipsNeeded = assignment.transferCount * 12;
          break;
        default:
          tipsNeeded = assignment.transferCount;
      }
      totalTipsByTipType.set(assignment.tipTypeId, current + tipsNeeded);
    }
  }

  // Allocate tipboxes based on total tips needed (order-independent)
  const tipboxIdsByTipType = new Map<string, string[]>();
  totalTipsByTipType.forEach((totalTips, tipTypeId) => {
    const tipboxIds = allocateTipboxesForTotalUsage(tipTypeId, totalTips);
    tipboxIdsByTipType.set(tipTypeId, tipboxIds);
  });

  // Assign each step to a tipbox by sequentially filling tipboxes
  // This is done in step order for consistent assignment, but count is order-independent
  // Assign steps to tipboxes using the grouped and sorted assignments
  // We process per tipTypeId to apply the sorting logic (1ch first)
  const assignmentsByTipType = new Map<string, typeof stepAssignments>();
  for (const assignment of stepAssignments) {
    const existing = assignmentsByTipType.get(assignment.tipTypeId) || [];
    existing.push(assignment);
    assignmentsByTipType.set(assignment.tipTypeId, existing);
  }

  assignmentsByTipType.forEach((assignments, tipTypeId) => {
    const tipboxIds = tipboxIdsByTipType.get(tipTypeId);
    if (!tipboxIds || tipboxIds.length === 0) return;

    // Sort: 1-channel patterns first to consolidate them into fewer tipboxes.
    // This helps heuristics that check "is 1ch touched" (which has stricter rules)
    // by keeping 1ch "contamination" contained to the minimal number of boxes.
    assignments.sort((a, b) => {
      const aIs1ch = a.pattern === "1x1";
      const bIs1ch = b.pattern === "1x1";
      if (aIs1ch && !bIs1ch) return -1;
      if (!aIs1ch && bIs1ch) return 1;
      return a.stepIndex - b.stepIndex; // Stable sort for same pattern
    });

    let currentBox = 0;
    let tipsUsedInCurrentBox = 0; // Track individual tips, not columns
    let partialColumnTips = 0; // Tips used in the current partial column (for 1ch)
    let hasPartialColumn = false; // Whether current box has a partial column (contaminated for 8ch/12ch)

    for (const assignment of assignments) {
      const is1ch = assignment.pattern === "1x1";

      // Calculate actual tips needed based on pattern
      let tipsNeeded: number;
      switch (assignment.pattern) {
        case "1x1":
          tipsNeeded = assignment.transferCount;
          break;
        case "8x1":
          tipsNeeded = assignment.transferCount * 8;
          break;
        case "1x12":
          tipsNeeded = assignment.transferCount * 12;
          break;
        default:
          tipsNeeded = assignment.transferCount;
      }

      if (is1ch) {
        // 1-channel: can use partial columns
        let tipsToAllocate = tipsNeeded;

        // First, try to fill the current partial column
        if (hasPartialColumn && partialColumnTips > 0) {
          const tipsAvailableInPartialColumn = 8 - partialColumnTips;
          const tipsFromPartialColumn = Math.min(tipsToAllocate, tipsAvailableInPartialColumn);
          partialColumnTips += tipsFromPartialColumn;
          tipsUsedInCurrentBox += tipsFromPartialColumn;
          tipsToAllocate -= tipsFromPartialColumn;

          // If we filled the partial column, it's no longer partial
          if (partialColumnTips === 8) {
            partialColumnTips = 0;
            hasPartialColumn = false;
          }
        }

        // If we still need more tips, check if they fit in the current box
        if (tipsToAllocate > 0) {
          const tipsAvailableInBox = TIPBOX_TOTAL_CAPACITY - tipsUsedInCurrentBox;

          if (tipsToAllocate > tipsAvailableInBox) {
            // Move to next tipbox
            currentBox++;
            tipsUsedInCurrentBox = 0;
            partialColumnTips = 0;
            hasPartialColumn = false;
          }

          // Allocate remaining tips
          tipsUsedInCurrentBox += tipsToAllocate;

          // Update partial column tracking
          const newPartialTips = tipsToAllocate % 8;
          if (newPartialTips > 0) {
            partialColumnTips = newPartialTips;
            hasPartialColumn = true;
          }
        }
      } else {
        // 8-channel or 12-channel: needs full, untouched columns
        const columnsNeeded = assignment.columnsUsed;

        // If there's a partial column, we can't use it for multi-channel
        // Calculate available full columns in current box
        let fullColumnsAvailable;
        if (hasPartialColumn) {
          // We have a contaminated column, so available columns = (total capacity - used tips) / 8, rounded down
          // But we need to account for the partial column taking up space
          const tipsAvailableInBox = TIPBOX_TOTAL_CAPACITY - tipsUsedInCurrentBox;
          fullColumnsAvailable = Math.floor(tipsAvailableInBox / 8);
        } else {
          const tipsAvailableInBox = TIPBOX_TOTAL_CAPACITY - tipsUsedInCurrentBox;
          fullColumnsAvailable = Math.floor(tipsAvailableInBox / 8);
        }

        if (columnsNeeded > fullColumnsAvailable) {
          // Move to next tipbox
          currentBox++;
          tipsUsedInCurrentBox = 0;
          partialColumnTips = 0;
          hasPartialColumn = false;
        }

        // Allocate full columns
        tipsUsedInCurrentBox += columnsNeeded * 8;
      }

      // Assign this step to the current tipbox
      const tipboxId = tipboxIds[Math.min(currentBox, tipboxIds.length - 1)];
      stepToTipboxInstance.set(assignment.stepIndex, tipboxId);
    }
  });
  // ========================================================================

  // Generate the required tip labware objects
  const tipLabware: LabwareObject[] = [];
  // Track counts per tip type label to generate incrementing, unique labels
  const tipLabelCounts = new Map<string, number>();

  // First, create all shared tipbox instances that were tracked
  sharedTipboxTracking.forEach((tracking, tipTypeId) => {
    const lwType = activeConfig.labware.find((t: any) => t.id === tipTypeId);
    const baseLabel = lwType?.label ?? tipTypeId;

    for (const tipboxId of tracking.instances) {
      const nextCount = (tipLabelCounts.get(baseLabel) || 0) + 1;
      tipLabelCounts.set(baseLabel, nextCount);

      const tipLabwareObject: LabwareObject = {
        id: tipboxId,
        typeId: tipTypeId,
        label: `${baseLabel} ${nextCount}`,
      };
      tipLabware.push(tipLabwareObject);
    }
  });

  // For each unique source and tip type combination, create tip labware
  // This handles: (1) non-shared types, (2) shared types WITHOUT capacity tracking (Felix)
  const sharedTipboxIdsWithoutTracking = new Map<string, string>(); // tipTypeId -> tipboxId for non-tracked shared tips

  sourceLabwareTipGroups.forEach((data, compositeKey) => {
    const isSharedTipType = isOnePerProtocol(
      data.requiredTipTypeId,
      activeConfig
    );
    const trackCapacity = shouldTrackTipCapacity(
      data.requiredTipTypeId,
      activeConfig
    );

    // Skip shared tip types WITH capacity tracking - they're already created above via sharedTipboxTracking
    if (isSharedTipType && trackCapacity) return;

    // For shared types WITHOUT capacity tracking (Felix): create ONE tipbox per type
    if (isSharedTipType && !trackCapacity) {
      // Check if we already created a tipbox for this shared type
      if (!sharedTipboxIdsWithoutTracking.has(data.requiredTipTypeId)) {
        const tipLabwareId = `tips-${tipLabware.length + 1}`;
        const lwType = activeConfig.labware.find(
          (t: any) => t.id === data.requiredTipTypeId
        );
        const baseLabel = lwType?.label ?? data.requiredTipTypeId;
        const nextCount = (tipLabelCounts.get(baseLabel) || 0) + 1;
        tipLabelCounts.set(baseLabel, nextCount);

        const tipLabwareObject: LabwareObject = {
          id: tipLabwareId,
          typeId: data.requiredTipTypeId,
          label: `${baseLabel} ${nextCount}`,
        };
        tipLabware.push(tipLabwareObject);
        sharedTipboxIdsWithoutTracking.set(data.requiredTipTypeId, tipLabwareId);
      }
      return; // Don't create per-source mapping for shared types
    }

    // Non-shared types: create tip labware per source
    let tipLabwareId = `tips-${tipLabware.length + 1}`; // Sequential unique ID

    // For 96-channel tip trays (Felix), try to use the type ID as the instance ID
    // This maintains compatibility with blocks that expect "tiptray-1000" (default param value)
    if (classifyTipTray(data.requiredTipTypeId, activeConfig) && !tipLabware.some(l => l.id === data.requiredTipTypeId)) {
      tipLabwareId = data.requiredTipTypeId;
    }

    // Label from config labware type, increment per type to ensure uniqueness
    const lwType = activeConfig.labware.find(
      (t: any) => t.id === data.requiredTipTypeId
    );
    const baseLabel = lwType?.label ?? data.requiredTipTypeId;
    const nextCount = (tipLabelCounts.get(baseLabel) || 0) + 1;
    tipLabelCounts.set(baseLabel, nextCount);

    // For non-shared tips, use source labware name
    const label = `${baseLabel} ${nextCount}`;

    const tipLabwareObject: LabwareObject = {
      id: tipLabwareId,
      typeId: data.requiredTipTypeId, // Use the typeId from config
      label,
    };

    tipLabware.push(tipLabwareObject);

    // For non-shared tips, maintain the original source-specific mapping
    const sourceId = data.sourceId;
    const tipType = data.requiredTipTypeId;
    sourceToTipMap.set(`${sourceId}:${tipType}`, tipLabwareId);
  });

  // Use recorded assignments to create mappings for shared tipboxes per tip type
  // Handles both: (1) capacity-tracked via stepToTipboxInstance
  //               (2) non-tracked (Felix) via sharedTipboxIdsWithoutTracking
  for (const a of stepAssignments) {
    if (isOnePerProtocol(a.tipTypeId, activeConfig)) {
      const trackCapacity = shouldTrackTipCapacity(a.tipTypeId, activeConfig);

      if (trackCapacity) {
        // Use per-step tipbox instance from capacity tracking
        const tipboxId = stepToTipboxInstance.get(a.stepIndex);
        if (tipboxId) {
          sourceToTipMap.set(`${a.sourceId}:${a.tipTypeId}`, tipboxId);
        }
      } else {
        // Felix: use the single shared tipbox for this type
        const tipboxId = sharedTipboxIdsWithoutTracking.get(a.tipTypeId);
        if (tipboxId) {
          sourceToTipMap.set(`${a.sourceId}:${a.tipTypeId}`, tipboxId);
        }
      }
    }
  }

  // Build a direct stepIndex -> tipLabwareId mapping using recorded assignments
  const stepToTipMap = new Map<number, string>();

  for (const a of stepAssignments) {
    let tipId: string | undefined;
    const isShared = isOnePerProtocol(a.tipTypeId, activeConfig);
    const trackCap = shouldTrackTipCapacity(a.tipTypeId, activeConfig);

    if (isShared) {
      if (trackCap) {
        // Use per-step tipbox instance
        tipId = stepToTipboxInstance.get(a.stepIndex);
      } else {
        // Felix: use the single shared tipbox
        tipId = sharedTipboxIdsWithoutTracking.get(a.tipTypeId);
      }
    } else {
      tipId = sourceToTipMap.get(`${a.sourceId}:${a.tipTypeId}`);
    }
    if (tipId) {
      // Only set if not already mapped - this prioritizes shared capacity-tracked tipboxes
      // (which are processed first) over non-shared source-based tips
      if (!stepToTipMap.has(a.stepIndex)) {
        stepToTipMap.set(a.stepIndex, tipId);
      }
    }
  }

  // Return the array of tip labware objects, source-to-tip map, and the per-step maps
  return { tipLabware, sourceToTipMap, stepToTipMap, stepTipUsageByUid };
}

/**
 * Checks if any step requires a tip box adapter.
 * This is determined by looking at the configured tip type for steps that use tips.
 * If any step specifies a tip type containing "tipbox" (case-insensitive), an adapter is considered required.
 * @param doc The editor document node containing steps
 * @param activeConfig The active system configuration
 * @returns Boolean indicating if a tip box adapter is required
 */
export function isTipboxAdapterRequired(
  doc: Node,
  activeConfig: SystemConfig | null
): boolean {
  if (!activeConfig || !activeConfig.steps) {
    return false;
  }

  let adapterRequired = false;

  doc.descendants((node: Node, _stepPos: number) => {
    if (adapterRequired) return false; // Exit early if adapter requirement already found

    if (node.type.name === "step") {
      const stepName = getStepName(node);
      const configStepId = node.attrs.configStepId as string | null;
      const stepConfig =
        (configStepId
          ? activeConfig.steps.find((s: ConfigStep) => s.id === configStepId)
          : undefined) ??
        activeConfig.steps.find((s: ConfigStep) => s.name === stepName);

      if (!stepConfig || !stepConfig.params) return true; // Continue searching descendants

      // Check if step has any tip parameters and if their typeId implies a tipbox
      const tipParamConfig = stepConfig.params.find((p) => p.role === "tips");

      if (
        tipParamConfig &&
        tipParamConfig.options &&
        tipParamConfig.options.length > 0
      ) {
        const tipTypeId = tipParamConfig.options[0]; // Assume first option is the relevant one
        if (classifyTipBox(tipTypeId, activeConfig)) {
          adapterRequired = true;
          return false; // Stop searching, adapter is required
        }
      }
    }
    return true; // Continue searching doc descendants
  });

  return adapterRequired;
}
